import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { action } from 'mobx';

import { Callout, Card, Flex, Icon, Spinner, Tab, Tabs, Text } from 'core/components';
import { Field, formConsumer, InputGroup, Switch, TextArea } from 'core/form';

import MetricsItemExplorer from 'app/views/core/dashboards/dashboardItem/form/MetricsItemExplorer';
import MetricsExplorerGuidedModeBehavior from 'app/views/core/dashboards/dashboardItem/form/MetricsExplorerGuidedModeBehavior';
import PanelTitleHelpText from './PanelTitleHelpText';
import GuidedModeBehavior from './form/GuidedModeBehavior';
import NavigateDashboard from './form/NavigateDashboard';
import ItemExplorer from './form/ItemExplorer';
import DashboardSelector from '../DashboardSelector';

@inject('$dictionary', '$dashboards', '$dashboard', '$explorer', '$auth')
@formConsumer
@observer
class DashboardItemForm extends Component {
  static defaultProps = {
    showDashboardSelect: true,
    showItemProperties: true,
    showDataviewConfiguration: true,
    p: 2
  };

  getIsParametricModeCompatiblePanel = () => {
    const { model, $auth } = this.props;
    const panelType = model.get('panel_type');
    const isMetricsExplorerGuidedModeFF = $auth.hasPermission('recon.guidedMetricsExplorer.enabled', {
      overrideForSudo: false
    });
    return panelType === 'explorer_query' || (isMetricsExplorerGuidedModeFF && panelType === 'metrics');
  };

  componentDidMount() {
    const { $dashboards, $explorer, form, model } = this.props;
    const dashboard = $dashboards.collection.get(form.getValue('dashboard_id'));
    const panelType = model.get('panel_type');

    if (
      dashboard &&
      dashboard.get('parametric') &&
      this.getIsParametricModeCompatiblePanel() &&
      form.getValue('parametric_mode') !== 'ignore'
    ) {
      form.getField('parametric_overrides.filterField').setRules('required');
      form.getField('parametric_overrides.operator').setRules('required');
    }

    if (panelType === 'explorer_query') {
      const overrides = {};

      if (dashboard) {
        const query = dashboard.get('query');

        if (!model.get('time_locked')) {
          overrides.lookback_seconds = query.lookback_seconds;
          overrides.starting_time = query.starting_time;
          overrides.ending_time = query.ending_time;
          overrides.time_format = query.time_format;
        }

        if (!model.get('device_locked')) {
          overrides.device_name = query.device_name || [];
          overrides.device_sites = query.device_sites || [];
          overrides.device_labels = query.device_labels || [];
          overrides.device_types = query.device_types || [];
          overrides.all_devices = query.all_devices;
        }

        if (model.get('filter_source') === 'dashboard') {
          overrides.filters = query.filters;
        }
      }

      $explorer.loadView(model, false, overrides);
    }
  }

  componentDidUpdate() {
    const { model } = this.props;

    if (model) {
      const queries = model.get('queries');
      if (queries && queries.length) {
        const timeLockedDisabled = queries[0].attributes.lookback_seconds === 0;
        this.handleTimeLockedField(timeLockedDisabled);
      }
    }
  }

  @action
  handleTimeLockedField = (timeLockedDisabled) => {
    const { form } = this.props;
    const field = form.getField('time_locked');

    field.options[1].disabled = !!timeLockedDisabled;

    field.options[1].helpText = timeLockedDisabled
      ? 'This option is only enabled when the panel is set to a Lookback Time Range'
      : "The time range represented in this panel's graph will be controlled by the panel's lookback timeframe.";

    if (timeLockedDisabled) {
      field.setValue(false);
    }
  };

  /**
   * Only gets used when DashboardItemModel.isNew, because otherwise this Form only appears
   * in the DashboardItemDetail dialog, which never changes Dashboards.
   */
  handleDashboardChange = (field, dashboardId) => {
    const { form, $dashboards, $dictionary } = this.props;
    const { queryFilters } = $dictionary.dictionary;

    const selectedDashboard = $dashboards.collection.get(dashboardId);
    const isParametricDashboard = selectedDashboard.get('parametric') && form.getValue('parametric_mode') !== 'ignore';
    const activeParametricType =
      selectedDashboard.get('parametric_fields') && selectedDashboard.get('parametric_fields')[0];
    const { availableFilters } = this.getAvailableFiltersConfig(activeParametricType);
    const shouldValidateParametricFields = this.getIsParametricModeCompatiblePanel() && isParametricDashboard;

    form.getField('parametric_overrides.filterField').setRules(shouldValidateParametricFields ? 'required' : '');
    form.getField('parametric_overrides.operator').setRules(shouldValidateParametricFields ? 'required' : '');

    if (shouldValidateParametricFields) {
      form.setValue(
        'parametric_overrides.filterField',
        availableFilters.length ? availableFilters[0].value : undefined
      );
      form.setValue('parametric_overrides.operator', queryFilters.parametricOperators[activeParametricType.type]);
    }
  };

  getAvailableFiltersConfig = (parametricType) => {
    const { $dictionary, $dashboards, form, model: item } = this.props;

    const selectedDashboard = $dashboards.collection.get(form.getValue('dashboard_id'));
    const { activeFilterFields } = item.get('queries')[0];

    const { parametric } = selectedDashboard.get();
    const { parametric_mode } = form.getValues();
    // we disable the `override_specific` option if there are no filters in this Items existing filters
    // that can be overridden by the selected parametric type
    const availableFilters = $dictionary.getAvailableFiltersForParametricType(parametricType);
    const canOverrideSpecific = availableFilters.some((filter) => activeFilterFields.includes(filter.value));

    if (canOverrideSpecific) {
      if (parametric && parametric_mode === 'override_specific') {
        const filters = availableFilters.filter((filter) => activeFilterFields.includes(filter.value));
        return { availableFilters: filters, canOverrideSpecific };
      }
    }
    return { availableFilters, canOverrideSpecific };
  };

  render() {
    const {
      model,
      form,
      $dashboards,
      $dictionary,
      onQueryChange,
      showDashboardSelect,
      showItemProperties,
      showDataviewConfiguration,
      innerFormSetter,
      p
    } = this.props;

    const {
      filterOperatorOptions,
      standardFilterOperators,
      dictionary: { queryFilters }
    } = $dictionary;

    const parametricMode = form.getValue('parametric_mode');
    const panelType = model.get('panel_type');

    if (!model.fetchedQuery) {
      return (
        <Flex alignItems="center" justifyContent="center" p={p}>
          <Spinner />
        </Flex>
      );
    }

    let availableFiltersConfig;
    let isParametric;
    let selectedDashboard;
    let parametricType;

    const formDashboard = form.getValue('dashboard_id');

    if (formDashboard) {
      selectedDashboard = $dashboards.collection.get(formDashboard);
      isParametric = selectedDashboard && selectedDashboard.get('parametric');

      if (isParametric) {
        const parametricFields = selectedDashboard.get('parametric_fields');
        parametricType = parametricFields && parametricFields[0];
        availableFiltersConfig = this.getAvailableFiltersConfig(parametricType);
      }
    }

    const isDataExplorerPanel = panelType === 'explorer_query';
    const isRawFlowPanel = panelType === 'raw_flow';
    const isMetricsPanel = panelType === 'metrics';
    const isParametricModeCompatiblePanel = this.getIsParametricModeCompatiblePanel();

    const DataViewConfigurationForm = model.formState.component;
    const shouldShowTabbedMetricsExplorerFlow = isParametricModeCompatiblePanel && isParametric && isMetricsPanel;
    const metricsExplorerDashboardMeasurement = model.editQuery?.get?.('kmetrics.measurement');
    const { editQuery } = model;

    const tabs = [
      shouldShowTabbedMetricsExplorerFlow && {
        id: 'query',
        group: 'dataview',
        title: 'Query',
        panel: (
          <Card overflow="hidden">
            <MetricsItemExplorer
              form={form}
              dashboardItem={model}
              dashboard={selectedDashboard}
              onQueryChange={onQueryChange}
              innerFormSetter={innerFormSetter}
            />
          </Card>
        ),
        p: 0
      },
      isDataExplorerPanel && {
        id: 'query',
        group: 'dataview',
        title: 'Query',
        panel: <ItemExplorer dashboard={selectedDashboard} onQueryChange={onQueryChange} />,
        p: 0
      },
      isRawFlowPanel && {
        id: 'rawFlow',
        group: 'dataview',
        title: 'Query',
        panel: (
          <Card p={2} mt={2}>
            <DataViewConfigurationForm form={form} dashboardItem={model} />
          </Card>
        ),
        p: 0
      },
      isParametric &&
        !isMetricsPanel && {
          id: 'guided',
          group: 'parametric',
          title: 'Guided Mode',
          icon: 'chat',
          panel: (
            <GuidedModeBehavior
              dashboardItem={model}
              measurement={metricsExplorerDashboardMeasurement}
              availableFiltersConfig={availableFiltersConfig}
              parametric_mode={parametricMode}
              filterOperatorOptions={filterOperatorOptions}
              standardFilterOperators={standardFilterOperators}
              queryFilters={queryFilters}
              form={form}
              boxProps={{}}
              panelType={panelType}
            />
          )
        },
      shouldShowTabbedMetricsExplorerFlow && {
        id: 'guided',
        group: 'parametric',
        title: 'Guided Mode',
        icon: 'chat',
        panel: (
          <MetricsExplorerGuidedModeBehavior
            dashboardItem={model}
            availableFiltersConfig={availableFiltersConfig}
            parametricMode={parametricMode}
            filterOperatorOptions={filterOperatorOptions}
            standardFilterOperators={standardFilterOperators}
            form={form}
            panelType={panelType}
            parametricType={parametricType}
            measurement={metricsExplorerDashboardMeasurement}
            filterFields={editQuery?.get('kmetrics.filters')}
          />
        )
      },
      isDataExplorerPanel && {
        id: 'navigate',
        group: 'nestedDashboard',
        title: 'Navigate To',
        icon: 'arrow-right',
        panel: <NavigateDashboard />
      }
    ].filter((tab) => Boolean(tab));

    return (
      <Flex flexDirection="column" flexAuto p={p}>
        <Field
          name="panel_title"
          helpText={model.dataview.isFlowVisualization && <PanelTitleHelpText parametric={isParametric} />}
          width={400}
          large
        >
          <InputGroup />
        </Field>

        <Field name="panel_description" mb={0} width={600} large>
          <TextArea rows={2} fill />
        </Field>

        {isDataExplorerPanel && (
          <Callout maxWidth={600} mt={2}>
            <Field name="panel_filtering" mb={0} large>
              <Switch showOptionLabel />
            </Field>
          </Callout>
        )}

        {showDashboardSelect && (
          <Card maxWidth={600} p={2} mt={2}>
            <Text fontWeight="bold" small>
              Dashboard this new panel will be added to
            </Text>
            <DashboardSelector
              name="dashboard_id"
              onChange={this.handleDashboardChange}
              onlyEditable
              showPresets={false}
              showLabel={false}
              mb={0}
              fill
              large
            />
          </Card>
        )}

        {model.formState.component && showDataviewConfiguration && !isParametric && (
          <Card p={2} mt={2}>
            <DataViewConfigurationForm form={form} dashboardItem={model} />
          </Card>
        )}

        {isMetricsPanel && !shouldShowTabbedMetricsExplorerFlow && (
          <Card mt={2} overflow="hidden">
            <MetricsItemExplorer
              form={form}
              dashboardItem={model}
              dashboard={selectedDashboard}
              onQueryChange={onQueryChange}
              innerFormSetter={innerFormSetter}
            />
          </Card>
        )}

        {showItemProperties &&
          (isDataExplorerPanel || (isRawFlowPanel && isParametric) || shouldShowTabbedMetricsExplorerFlow) && (
            <Tabs mt={3}>
              {tabs.map((tab) => (
                <Tab
                  key={tab.id}
                  id={tab.id}
                  title={
                    <Flex alignItems="center" justifyContent="center" px={1}>
                      <Icon icon={tab.icon} mr={1} />
                      {tab.title}
                    </Flex>
                  }
                  panel={
                    <Card flex={1} p={tab.p !== undefined ? tab.p : 2}>
                      {tab.panel}
                    </Card>
                  }
                />
              ))}
            </Tabs>
          )}
      </Flex>
    );
  }
}

export default DashboardItemForm;
